/**
 * AVS Gold ERP — Jelly-Style Modular Architecture Definitions
 *
 * Implements the 14 Enterprise Modules and their hierarchical sub-feature flags
 * as specified in the authoritative Administration Architecture:
 *
 * MODULE -> FEATURE -> PERMISSION -> ROLE -> USER
 *
 * Toggling modules enables/disables visibility and operational access without destroying data.
 */

export interface JellyFeature {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
}

export interface JellyModule {
  id: string;
  name: string;
  category: "Core" | "Operations" | "Finance" | "Tools" | "Platform";
  description: string;
  enabled: boolean;
  isCore?: boolean;
  features: JellyFeature[];
}

export const INITIAL_JELLY_MODULES: JellyModule[] = [
  {
    id: "core",
    name: "Core Administration",
    category: "Core",
    description: "Company identity, security policies, multi-branch, users & role-based permissions.",
    enabled: true,
    isCore: true,
    features: [
      { id: "company_profile", name: "Company Profile & Identity", description: "Firm legal name, GSTIN, PAN, and trade marks", enabled: true },
      { id: "users_roles", name: "User Management & RBAC", description: "Granular operator, manager, accountant, artisan roles", enabled: true },
      { id: "security_locks", name: "Configuration Locks & Audit", description: "Cryptographic tamper monitoring and state locking", enabled: true },
    ],
  },
  {
    id: "manufacturing",
    name: "Jewellery Manufacturing & Workshop",
    category: "Operations",
    description: "Crucible melting, artisan job cards, karigar custody, outside work, and production loss.",
    enabled: true,
    features: [
      { id: "melting_furnace", name: "Melting Furnace & Recovery", description: "Crucible burning loss, touch testing, and bar casting", enabled: true },
      { id: "job_cards", name: "Artisan Job Cards", description: "Bespoke job cards, purity targets, and material slips", enabled: true },
      { id: "karigar_custody", name: "Karigar Bench Custody", description: "Artisan metal custody tracking and daily filings return", enabled: true },
      { id: "outside_work", name: "Outside Worker Jangad", description: "Diamond setting, meenakari, and hallmarking outside slips", enabled: true },
      { id: "production_loss", name: "Wastage & Loss Tolerances", description: "Artisan dust recovery vs allowable loss thresholds", enabled: true },
    ],
  },
  {
    id: "inventory",
    name: "Vault & Stock Inventory",
    category: "Operations",
    description: "Category tree, gross/net/fine weight, stone deductions, HUID tracking, and barcode tags.",
    enabled: true,
    features: [
      { id: "vault_stock", name: "Vault Metal & Item Ledger", description: "Multi-category bullion, raw metal, and finished stock", enabled: true },
      { id: "barcode_tagging", name: "Barcode & RFID Tagging", description: "Custom label formats, 1D/2D barcodes, and fast scanning", enabled: true },
      { id: "stock_audit", name: "Physical Stock Verification", description: "Periodic discrepancy audits and reconciliation", enabled: true },
    ],
  },
  {
    id: "billing",
    name: "Sales & Invoicing",
    category: "Operations",
    description: "Pre-payment checkout waterfall, GST tax invoices, estimates, quotations, and credit notes.",
    enabled: true,
    features: [
      { id: "tax_invoices", name: "GST Tax Invoices (A4 & POS)", description: "Tax-compliant billing with GST 3% itemized breakdown", enabled: true },
      { id: "estimates", name: "Quotations & Estimates", description: "Provisional customer estimates with live gold rates", enabled: true },
      { id: "checkout_waterfall", name: "Pre-Payment Checkout Waterfall", description: "Gross -> Discount -> Taxable -> GST -> Old Gold deduction", enabled: true },
      { id: "old_gold_exchange", name: "Old Gold / URD Inward", description: "Melt assay deduction and cash/metal settlement against bill", enabled: true },
    ],
  },
  {
    id: "accounting",
    name: "Financial & Metal Accounting",
    category: "Finance",
    description: "Double-entry Rojmel, Cash book, Bank reconciliation, Karigar Khata, and Trial Balance.",
    enabled: true,
    features: [
      { id: "double_entry_rojmel", name: "Rojmel & Daybook", description: "Simultaneous Cash (Rupees) and Metal (Fine Gold mg) balancing", enabled: true },
      { id: "karigar_khata", name: "Karigar Metal & Labour Khata", description: "Artisan metal balance in 995 benchmark + rupee labour dues", enabled: true },
      { id: "trial_balance", name: "Trial Balance & Financials", description: "Real-time debit/credit equality checks and balance sheets", enabled: true },
    ],
  },
  {
    id: "gst",
    name: "Statutory Tax & GST Engine",
    category: "Finance",
    description: "GST 3% jewellery rules, HSN summary, GSTR-1, GSTR-3B, and ITC-04 job work returns.",
    enabled: true,
    features: [
      { id: "gst_rates", name: "GST 3% Rate Engine", description: "Standardised 1.5% CGST + 1.5% SGST jewellery compliance", enabled: true },
      { id: "itc04_jobwork", name: "ITC-04 Artisan Challans", description: "Quarterly job-work movement tracking of gold inputs/outputs", enabled: true },
      { id: "e_invoicing", name: "E-Invoice & E-Way Bill", description: "IRN generation and QR code payload integration", enabled: true },
    ],
  },
  {
    id: "melting",
    name: "Melting & Touch Refinery",
    category: "Operations",
    description: "Old gold scrap crucible runs, purity assay testing, touch calculation, and bar settlement.",
    enabled: true,
    features: [
      { id: "crucible_heat", name: "Furnace Heat Logging", description: "Operator, temperature, flux additives, and melt yield", enabled: true },
      { id: "assay_lab", name: "Touch & XRF Assay Registry", description: "Certified purity assay matching vs fine metal settlement", enabled: true },
    ],
  },
  {
    id: "bullion",
    name: "Bullion & Daily Gold Rate Desk",
    category: "Operations",
    description: "Multi-touch daily market rates, IBJA live ticker, board rate cut, and premium/discount.",
    enabled: true,
    features: [
      { id: "daily_bhav", name: "Daily Bhav / Board Rate", description: "24K (999), 22K (916), 18K (750) buying and selling rates", enabled: true },
      { id: "rate_cut_booking", name: "Customer Rate-Cut Booking", description: "Advance metal fixing against order deposits", enabled: true },
    ],
  },
  {
    id: "crm",
    name: "Customer Relationship (CRM 360)",
    category: "Operations",
    description: "Client profiles, gold savings schemes, VIP loyalty, payment links, and KYC compliance.",
    enabled: true,
    features: [
      { id: "gold_schemes", name: "Gold Savings Schemes", description: "Monthly installment accumulation in rupee and gold weight", enabled: true },
      { id: "customer_portal", name: "Customer Self-Service Portal", description: "Passbook, order tracker, digital receipts, and UPI payments", enabled: true },
      { id: "kyc_docs", name: "PAN / Aadhaar KYC Compliance", description: "High-value purchase statutory PAN verification (> Rs 2 Lakh)", enabled: true },
    ],
  },
  {
    id: "hr",
    name: "HR, Karigars & Payroll",
    category: "Operations",
    description: "Staff attendance, artisan piece-rate compensation, wage advances, and TDS registers.",
    enabled: true,
    features: [
      { id: "attendance", name: "Daily Artisan Attendance", description: "Biometric and manual shift logs for workshop craftsmen", enabled: true },
      { id: "piece_rate_wages", name: "Weight-Based Piece Wages", description: "Compensation per gram of manufactured gold or stone count", enabled: true },
      { id: "salary_advances", name: "Advance & Deduction Khata", description: "Festival advances and monthly salary balance settlement", enabled: true },
    ],
  },
  {
    id: "hardware",
    name: "Hardware & POS Peripherals",
    category: "Tools",
    description: 'RS232 weighing scales, thermal receipt printers, 2"x1" jewellery tag printers, and scanners.',
    enabled: true,
    features: [
      { id: "weighing_scale", name: "Direct Weighing Scale (COM/USB)", description: "Auto-reads gross/net weight with 0.001g milligram precision", enabled: true },
      { id: "barcode_printers", name: "Thermal Label / Tag Printers", description: "Direct ZPL/TSPL rat-tail tag generation for rings and chains", enabled: true },
      { id: "thermal_receipts", name: "3-Inch Thermal Receipt Printer", description: "Instant counter slip printing with QR code", enabled: true },
    ],
  },
  {
    id: "communication",
    name: "Communications Hub",
    category: "Tools",
    description: "Direct Hostinger SMTP dispatch, WhatsApp Business Cloud API, and PDF invoice dispatch.",
    enabled: true,
    features: [
      { id: "hostinger_smtp", name: "Direct Hostinger SMTP Engine", description: "Reliable SSL/TLS dispatch for invoices, OTPs, and digests", enabled: true },
      { id: "whatsapp_cloud", name: "Customer-Owned WhatsApp API", description: "Zero internal credit markup; customer-controlled API keys", enabled: true },
    ],
  },
  {
    id: "ai_mcp",
    name: "AI & MCP Tool Automation",
    category: "Tools",
    description: "Local Ollama LLM assistant, Model Context Protocol (MCP) server, and automated workflows.",
    enabled: true,
    features: [
      { id: "ollama_assistant", name: "Local Ollama AI Assistant", description: "Zero cloud data egress natural language query and reporting", enabled: true },
      { id: "mcp_server", name: "Model Context Protocol Server", description: "Standardized tools for Claude, Gemini, and local agents", enabled: true },
      { id: "daily_close_automation", name: "Scheduled Daily Daybook Close", description: "Automated end-of-day trial balance audit and snapshot", enabled: true },
    ],
  },
  {
    id: "security",
    name: "Security, Audit & Resilience",
    category: "Core",
    description: "AES-256-GCM data encryption, anti-tamper monitoring, and automated daily backups.",
    enabled: true,
    isCore: true,
    features: [
      { id: "aes_encryption", name: "Encrypted Storage", description: "Client-owned master password encryption", enabled: true },
      { id: "tamper_monitoring", name: "Runtime Tamper Detection", description: "Verifies HMAC and file integrity across all partitions", enabled: true },
      { id: "automated_backup", name: "Daily Rolling Backup Envelopes", description: "Compressed, encrypted archival to local or external storage", enabled: true },
    ],
  },
];

export interface SystemAdministrationState {
  isLocked: boolean;
  lockTimestamp?: string;
  activeModules: JellyModule[];
}

const STORAGE_KEY = "avs_jelly_admin_state_v1";

export function loadJellyAdminState(): SystemAdministrationState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      return JSON.parse(raw);
    }
  } catch (e) {
    // fallback
  }
  return {
    isLocked: true,
    activeModules: INITIAL_JELLY_MODULES,
  };
}

export function saveJellyAdminState(state: SystemAdministrationState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    console.error("Failed to save Jelly admin state:", e);
  }
}
